import React, { useState } from "react";
import { useQuery } from "react-query";
import apiClient from "../../../services/apiClient";
import { useTranslation } from "react-i18next";
import {
  Backdrop,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Paper,
} from "@mui/material";
import { BaseGrid, NotificationAlert, Title } from "../../index";
import { format, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";

function NotificationLogs() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [logs, setLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);

  // Récupérer les dates disponibles
  const { isLoading: isLoadingDates } = useQuery(
    ["fetchAvailableNotificationLogDates"],
    async () => {
      return await apiClient.fetchAvailableNotificationLogDates();
    },
    {
      enabled: true,
      onSuccess: ({ data }) => {
        setAvailableDates(data || []);
        // Sélectionner la date la plus récente par défaut
        if (data && data.length > 0 && !selectedDate) {
          setSelectedDate(data[0]);
        }
      },
      onError: (error) => {
        setErrors({ msg: true });
      },
    }
  );

  // Récupérer les logs pour la date sélectionnée
  const { isLoading, refetch: fetchNotificationLogs } = useQuery(
    ["fetchNotificationLogs", selectedDate],
    async () => {
      return await apiClient.fetchNotificationLogs(selectedDate);
    },
    {
      enabled: !!selectedDate,
      onSuccess: ({ data }) => {
        setLogs(data || []);
      },
      onError: (error) => {
        setErrors({ msg: true });
      },
      refetchInterval: 5000, // Rafraîchir toutes les 5 secondes pour voir les nouveaux logs en temps réel
    }
  );

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const getStatusColor = (status) => {
    return status === "SUCCESS" ? "success" : "error";
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "SMS":
        return "primary";
      case "VOICE":
        return "secondary";
      case "EMAIL":
        return "info";
      default:
        return "default";
    }
  };

  const gridColumns = [
    {
      name: "type",
      label: t("log.Type") || "Type",
      options: {
        customBodyRender: (value) => (
          <Chip label={value || ""} color={getTypeColor(value)} size="small" />
        ),
      },
    },
    {
      name: "status",
      label: t("log.Status") || "Statut",
      options: {
        customBodyRender: (value) => (
          <Chip
            label={
              value === "SUCCESS"
                ? t("log.Success") || "Succès"
                : t("log.Failed") || "Échec"
            }
            color={getStatusColor(value)}
            size="small"
          />
        ),
      },
    },
    {
      name: "recipient",
      label: t("log.Recipient") || "Destinataire",
    },
    {
      name: "message",
      label: t("log.Message") || "Message",
      options: {
        customBodyRender: (value) => (
          <Box
            sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {value || ""}
          </Box>
        ),
      },
    },
    {
      name: "error",
      label: t("log.Error") || "Erreur",
      options: {
        customBodyRender: (value) =>
          value ? (
            <Box
              sx={{
                maxWidth: 300,
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "error.main",
              }}
            >
              {value}
            </Box>
          ) : (
            "-"
          ),
      },
    },
  ];

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading || isLoadingDates}
      >
        <CircularProgress color="secondary" size={40} />
      </Backdrop>
      <NotificationAlert
        openNotification={errors?.msg}
        type="error"
        message={
          t("log.ErrorFetchingData") ||
          "Erreur lors de la récupération des données"
        }
      />
      <Container maxWidth="xl" sx={{ pt: 5, pb: 5 }}>
        <Title
          handleAdd={""}
          pageTitle={t("log.NotificationLogsTitle") || "Logs de Notifications"}
          iconTooltip={""}
          noAdd={true}
        />

        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 300 }}>
            <InputLabel id="date-select-label">
              {t("log.SelectDate") || "Sélectionner une date"}
            </InputLabel>
            <Select
              labelId="date-select-label"
              id="date-select"
              value={selectedDate || ""}
              label={t("log.SelectDate") || "Sélectionner une date"}
              onChange={handleDateChange}
            >
              {availableDates.map((date) => {
                try {
                  const dateObj = parseISO(date + "T00:00:00");
                  return (
                    <MenuItem key={date} value={date}>
                      {format(dateObj, "PPP", { locale: frCA })}
                    </MenuItem>
                  );
                } catch (e) {
                  return (
                    <MenuItem key={date} value={date}>
                      {date}
                    </MenuItem>
                  );
                }
              })}
            </Select>
          </FormControl>
        </Paper>

        {selectedDate && <BaseGrid gridData={logs} gridColumn={gridColumns} />}
      </Container>
    </>
  );
}

export default NotificationLogs;
